import type { ForwardedRef, MouseEventHandler } from 'react';
import { forwardRef } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import type { DefaultComponentProps } from '../../types';
import { useSocialFeedSettings } from '../../utils';

interface LinkProps extends DefaultComponentProps {
  href?: string;
  color?: string;
  hoverEffect?: CSSProperties['textDecoration'];
}

export const Link = forwardRef<HTMLDivElement | HTMLAnchorElement, LinkProps>(
  (
    { children, href, color, hoverEffect = 'underline', ...forwardingProps },
    ref
  ) => {
    const { enableExternalLinks, openLinksNewTab } = useSocialFeedSettings();
    const isClickable = enableExternalLinks && !!href;

    const handleClick: MouseEventHandler = (e) => {
      e.stopPropagation();
    };

    if (isClickable) {
      return (
        <A
          {...forwardingProps}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          href={href}
          onClick={handleClick}
          target={openLinksNewTab ? '_blank' : '_top'}
          rel="noopener noreferrer nofollow"
          _hoverEffect={hoverEffect}
          _color={color}
        >
          {children}
        </A>
      );
    }

    return (
      <Container {...forwardingProps} ref={ref as ForwardedRef<HTMLDivElement>}>
        {children}
      </Container>
    );
  }
);

const A = styled.a<{
  _hoverEffect: CSSProperties['textDecoration'];
  _color?: string;
}>`
  display: block;
  text-decoration: none;
  border: none;
  color: ${({ _color }) => _color || 'inherit'};

  &:hover {
    text-decoration: ${({ _hoverEffect }) => _hoverEffect};
  }
`;

const Container = styled.div`
  max-width: 100%;
`;
